/**
 * THE LONGITUDINAL ASPECT STUDY, headless.
 *
 * <p>The first toolbar icon — "Study of well known longitudinal aspects as well as aspects of any
 * angular distance between any two planets" — runs `LongitudeStudyDlg` against
 * `GET /api/followstudy/get-degree-separation`. This module is the same study with no dialog
 * mounted: resolve the parameters, build the request, build the overlays.
 *
 * <p>`LongitudeStudyDlg` keeps its own copy of none of this. The builders here are what the
 * headless path uses, and the shapes they emit are the shapes the dialog already emits, so a
 * command-driven run and a hand-driven one paint the same marks.
 *
 * <h2>⚠ TWO OF THE SPECIFIED DEFAULTS HAVE NOWHERE TO GO, and saying so is the point</h2>
 *
 * The endpoint takes `planet1, planet2, degrees, fromDate, toDate, frame` and nothing else:
 *
 * <pre>
 *   ORB                 NOT A PARAMETER. This study locates an EXACT crossing by sign change
 *                       and bisection, not a tolerance window -- the same rule the eclipse and
 *                       midpoint-trine scanners state. There is no orb to default.
 *   SIDEREAL / TROPICAL NOT A PARAMETER, and could not change the answer if it were: the
 *                       ayanamsha appears in both longitudes of a SEPARATION and cancels. The
 *                       frame axis here is geocentric vs heliocentric.
 * </pre>
 *
 * <p>So an orb is ACCEPTED and reported in {@code ignored} with its reason, rather than dropped.
 * A dropped parameter is the `buildSimulationQuery` defect — a lever accepted and never applied,
 * with a correct-looking answer to a question nobody asked. And an explanation claiming "±1.0°
 * orb" when no tolerance was applied would be worse than either, because the reader would believe
 * a window they did not get.
 */

/** The well-known aspects, by name. Any angle is legal; these are the ones a sentence names. */
export const ASPECT_ANGLES = Object.freeze({
  conjunction: 0,
  sextile: 60,
  square: 90,
  trine: 120,
  opposition: 180,
});

/** The reverse map, for naming an angle back in an explanation. */
export const ASPECT_NAME_BY_ANGLE = Object.freeze(
  Object.fromEntries(Object.entries(ASPECT_ANGLES).map(([n, a]) => [a, n])),
);

/**
 * Domain defaults applied when a sentence does not carry one.
 *
 * <p>`aspectAngle` is CONJUNCTION because that is the aspect a bare "Sun and Mars" means, and
 * `body2` is the Sun because a single named body is being asked about relative to the chart's
 * reference — both as specified.
 */
export const ASPECT_DEFAULTS = Object.freeze({
  aspectAngle: ASPECT_ANGLES.conjunction,
  body2: 'Sun',
  frame: 'geocentric',
});

/** The two frames the endpoint accepts. Sidereal/tropical is not one of them — see the header. */
export const ASPECT_FRAMES = Object.freeze(['geocentric', 'heliocentric']);

/** Canonical capitalisation — the backend matches planet names case-sensitively. */
const titleCase = (s) => String(s).trim().replace(/\s+/g, ' ')
  .replace(/^(.)(.*)$/, (_, a, b) => a.toUpperCase() + b.toLowerCase());

/**
 * An angle from a name, a number, or a string holding either.
 *
 * <p>Returns null rather than guessing: an unparseable aspect must be refused with the valid set
 * listed, not silently turned into a conjunction, which would run a study nobody asked for under
 * a name they did ask for.
 */
export function angleFor(aspect) {
  if (aspect === undefined || aspect === null || aspect === '') return null;
  if (typeof aspect === 'number' && Number.isFinite(aspect)) return aspect;
  const raw = String(aspect).trim().toLowerCase();
  if (raw in ASPECT_ANGLES) return ASPECT_ANGLES[raw];
  const n = Number(raw.replace(/\s*(deg(rees)?|°)\s*$/, ''));
  return Number.isFinite(n) ? n : null;
}

/** How an angle reads in prose: "square (90°)" or just "137°". */
export const describeAngle = (a) =>
  (ASPECT_NAME_BY_ANGLE[a] ? `${ASPECT_NAME_BY_ANGLE[a]} (${a}°)` : `${a}°`);

/**
 * Resolve a natural-language request into endpoint parameters, naming every default applied.
 *
 * @param req `{ body1, body2?, aspectAngle?, orb?, frame? }`
 * @param ctx `{ frame? }` — the chart's active frame, when there is one
 * @returns `{ ok, params?, applied, ignored, problem? }`
 */
export function resolveAspectParams(req = {}, ctx = {}) {
  const applied = [];
  const ignored = [];

  const body1 = req.body1 ? titleCase(req.body1) : null;
  if (!body1) {
    return { ok: false, applied, ignored, problem: 'No body named. An aspect needs at least one.' };
  }

  let body2 = req.body2 ? titleCase(req.body2) : null;
  if (!body2) {
    body2 = ASPECT_DEFAULTS.body2;
    applied.push(`second body defaulted to ${body2}`);
  }
  if (body1 === body2) {
    // The dialog refuses this too: a body's separation from itself is zero on every bar, so the
    // study would mark the entire series or nothing at all depending on the angle.
    return { ok: false, applied, ignored,
      problem: `${body1} cannot be aspected to itself — name a second body.` };
  }

  let aspectAngle = angleFor(req.aspectAngle);
  if (aspectAngle === null) {
    if (req.aspectAngle !== undefined && req.aspectAngle !== null && req.aspectAngle !== '') {
      return { ok: false, applied, ignored,
        problem: `"${req.aspectAngle}" is not an aspect. Name one of `
          + `${Object.keys(ASPECT_ANGLES).join(', ')}, or give an angle in degrees.` };
    }
    aspectAngle = ASPECT_DEFAULTS.aspectAngle;
    applied.push(`aspect defaulted to ${describeAngle(aspectAngle)}`);
  }
  if (!(aspectAngle >= 0 && aspectAngle <= 360)) {
    return { ok: false, applied, ignored,
      problem: `${aspectAngle}° is outside 0–360.` };
  }

  const wanted = req.frame ? String(req.frame).trim().toLowerCase() : null;
  let frame;
  if (wanted && ASPECT_FRAMES.includes(wanted)) {
    frame = wanted;
  } else if (wanted) {
    // "geocentric sidereal" and "tropical" reach here. The leading word still decides the only
    // axis this endpoint has; the rest is recorded as ignored rather than silently honoured.
    frame = wanted.startsWith('helio') ? 'heliocentric' : 'geocentric';
    ignored.push(`"${req.frame}" — this study's frame axis is geocentric or heliocentric; `
      + 'sidereal and tropical cannot change a separation, because the ayanamsha cancels');
  } else {
    frame = ASPECT_FRAMES.includes(String(ctx.frame || '').toLowerCase())
      ? String(ctx.frame).toLowerCase()
      : ASPECT_DEFAULTS.frame;
    applied.push(`frame defaulted to ${frame}`);
  }

  if (req.orb !== undefined && req.orb !== null && req.orb !== '') {
    ignored.push(`orb ${req.orb}° — this study locates EXACT crossings by bisection and takes `
      + 'no tolerance, so no orb was applied');
  }

  return {
    ok: true,
    applied,
    ignored,
    params: { planet1: body1, planet2: body2, degrees: aspectAngle, frame },
  };
}

/** The query string for `/api/followstudy/get-degree-separation`, bounded by the loaded bars. */
export function aspectQuery(params, barData) {
  const dates = barData?.dates;
  if (!Array.isArray(dates) || dates.length === 0) return null;
  const ymd = (d) => String(d).split('T')[0];
  return new URLSearchParams({
    planet1: params.planet1,
    planet2: params.planet2,
    degrees: String(params.degrees),
    fromDate: ymd(dates[0]),
    toDate: ymd(dates[dates.length - 1]),
    frame: params.frame,
  });
}

/** A one-line label, used on every mark so the chart says which study drew it. */
export const aspectLabel = (p) =>
  `${p.planet1}-${p.degrees}-${p.planet2} (${p.frame === 'heliocentric' ? 'H' : 'G'})`;

/**
 * Vertical-line overlays, the same shape `LongitudeStudyDlg` hands to `onRun`.
 *
 * <p>Vertical lines rather than dots, deliberately: a dot anchors to a bar's high or low and
 * needs the bar, where an aspect is a DATE and carries no price — the same reasoning the analog
 * scanner's rail marks and the CTS forward marks already follow.
 */
export function aspectOverlays(dates, params, { color = '#1976d2', lineType = 'solid' } = {}) {
  return (dates || []).map((d) => {
    const date = String(d).split('T')[0];
    return {
      type: 'verticalLine',
      planet1: params.planet1,
      planet2: params.planet2,
      degrees: params.degrees,
      displayType: 'verticalLine',
      color,
      thickness: 2,
      frameOfReference: params.frame === 'heliocentric' ? 'Heliocentric' : 'Geocentric',
      lineType,
      label: `${aspectLabel(params)}\n${date}`,
      date,
    };
  });
}

/** The sentence a caller can put in front of a reader, naming every default and every omission. */
export function explainAspectRun(params, { applied = [], ignored = [] } = {}, count = null) {
  const head = `${params.planet1} ${describeAngle(params.degrees)} ${params.planet2}`;
  const bits = [];
  if (applied.length) bits.push(`defaults: ${applied.join('; ')}`);
  if (ignored.length) bits.push(`not applied: ${ignored.join('; ')}`);
  const found = count === null ? '' : ` ${count} date${count === 1 ? '' : 's'} marked.`;
  return `${head}${bits.length ? ` (${bits.join(' · ')})` : ''}.${found}`;
}
